import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { QueryClient } from '@tanstack/react-query'
import { APIProvider as GoogleMapsAPIProvider } from '@vis.gl/react-google-maps'
import { fr } from 'date-fns/locale/fr'
import createSimpleRestProvider from 'ra-data-simple-rest'
import * as React from 'react'
import { useEffect, useLayoutEffect, useState } from 'react'
import { Admin, CustomRoutes, localStorageStore, Resource } from 'react-admin'
import { createBrowserRouter, Route, RouterProvider } from 'react-router-dom'

import createAccountsProvider, { AccountsProvider } from './api/accountsProvider'
import createApiClient from './api/apiClient'
import createApiProvider, { ApiProvider } from './api/apiProvider'
import createAuthProvider from './api/authProvider'
import createDataProvider from './api/dataProvider'
import firebaseClient from './api/firebaseClient'
import createLocalApiClient from './api/localApiClient'
import createLocalAuthProvider from './api/localAuthProvider'
import createPermissionsProvider from './api/permissionsProvider'
import Layout from './components/Layout'
import ResetPage from './components/login/ResetPage'
import NotFound from './components/NotFound'
import env from './config/env'
import i18nProvider from './config/i18nProvider'
import './config/overrides'
import {
  SYSTEM_PERMISSION_CREATE,
  SYSTEM_PERMISSION_LIST,
  SYSTEM_PERMISSION_READ,
  SYSTEM_PERMISSION_SHOW,
  SYSTEM_PERMISSION_UPDATE,
  UI_PERMISSION_MENU,
} from './config/permissions'
import lightTheme, { darkTheme } from './config/theme'
import { hasResourcePermission } from './domain/permissions'
import resources from './resources'
import { AccessGroupsPage } from './resources/accessGroups/page'
import { AccountEditPage } from './resources/accounts/edit'
import { AccountShowPage } from './resources/accounts/show'
import { LoginPage } from './resources/common/login'
import { PayoutShowScreen } from './resources/payout/screens/show'
import { StatisticsPage } from './resources/statistics/page'
import vehiclesConfig from './resources/vehicles/config'
import { VehiclesTimelinePage } from './resources/vehicles/timeline'

import './vendor'

const baseTitle = document.title
export const useDocumentTitle = (funcOrString, displayBaseTitle = false) =>
  useLayoutEffect(() => {
    const documentTitle = typeof funcOrString === 'function' ? funcOrString() : funcOrString
    document.title = (documentTitle ?? '') + (displayBaseTitle ? ' - ' + baseTitle : '')
  })

const extractSlugFromPath = (path) => {
  const pathname = path.includes('://') ? new URL(path).pathname : path.split('?')[0].split('#')[0]
  return pathname.split('/')[1] || ''
}
const getResourceWithPermissions = (resource, permissions) => {
  const resourceName = resource.name.split('/')[0]
  const hasPermission = (permission) => hasResourcePermission(permissions, resourceName, permission)
  if (!hasPermission(SYSTEM_PERMISSION_READ)) {
    return null
  }
  const { list, show, create, edit, ...rest } = resource
  const props = {
    ...rest,
    list: (hasPermission(SYSTEM_PERMISSION_LIST) ? list : null) || null,
    show: (hasPermission(SYSTEM_PERMISSION_SHOW) ? show : null) || null,
    create: (hasPermission(SYSTEM_PERMISSION_CREATE) ? create : null) || null,
    edit: (hasPermission(SYSTEM_PERMISSION_UPDATE) ? edit : null) || null,
  }
  return <Resource {...props} />
}

const initialPath = window.location.pathname
const store = localStorageStore()
store.setItem('sidebar.open', false)

const customRoutes = [
  { element: ResetPage, noLayout: true },
  { element: AccountShowPage },
  { element: AccountEditPage },
  { element: PayoutShowScreen },
  { element: VehiclesTimelinePage },
  { element: StatisticsPage },
  { element: AccessGroupsPage },
]

const App = () => {
  const [state, setState] = useState(null)
  const routerRef = React.useRef(null)

  useEffect(() => {
    async function start() {
      const isLocalDev = !!env.LOCAL_TOKEN_OVERRIDE
      const baseApiClient = isLocalDev ? createLocalApiClient() : createApiClient(firebaseClient)
      const accountsProvider = createAccountsProvider(env.API_URL, baseApiClient)
      const apiClient = accountsProvider.client

      const restDataProvider = createSimpleRestProvider(env.API_URL, apiClient)
      const apiProvider = createApiProvider(env.API_URL, apiClient)
      const dataProvider = createDataProvider(restDataProvider, apiProvider)

      let basename
      let currentAccount = await accountsProvider.getCurrentAccount()
      if (currentAccount) {
        const initialSlug = extractSlugFromPath(initialPath)
        if (initialSlug !== currentAccount.slug) {
          // slug is different from current account (could be empty on first run)
          const slugAccount = accountsProvider.findAccountBySlug(initialSlug)
          if (slugAccount) {
            // we found a valid account with the specified slug --> set current account as the newly found account
            store.removeItem('timeline')
            resources.forEach((resource) => {
              const key = `${resource.name}.listParams`
              store.removeItem(key)
            })
            accountsProvider.setCurrentAccount(slugAccount)
            currentAccount = slugAccount
          } else {
            // no account with specified slug, use slug from current account
            if (initialSlug !== '' && initialSlug !== 'login' && initialSlug !== 'reset-password') {
              // a slug was present & different than base login URLs, but invalid
              window.alert("You don't have access to this account")
            }
            accountsProvider.navigateToCurrentAccount()
            return
          }
        }
        basename = '/' + currentAccount.slug
      } else {
        basename = undefined
      }

      const permissionsProvider = createPermissionsProvider(apiProvider)
      const authProvider = isLocalDev
        ? createLocalAuthProvider(accountsProvider, permissionsProvider)
        : createAuthProvider(firebaseClient, accountsProvider, baseApiClient, permissionsProvider, [ResetPage.path])

      // Redirect to vehicles timeline if user has READ permission for vehicles
      // Otherwise find the first resource with READ & MENU permissions and redirect to it
      if (currentAccount) {
        const pathRegex = new RegExp(`^/?${currentAccount.slug}/?$`)
        const isRootPath = pathRegex.test(window.location.pathname)
        if (isRootPath) {
          const permissions = await permissionsProvider.get()
          const hasReadForVehicles = hasResourcePermission(permissions, vehiclesConfig.name, SYSTEM_PERMISSION_READ)
          if (hasReadForVehicles) {
            window.history.pushState(null, '', basename + VehiclesTimelinePage.path)
          } else {
            const firstResource = resources.find((r) =>
              hasResourcePermission(permissions, r.name, SYSTEM_PERMISSION_READ, UI_PERMISSION_MENU),
            )
            if (firstResource) {
              window.history.pushState(null, '', basename + '/' + firstResource.name)
            }
          }
        }
      }

      setState({ basename, apiProvider, accountsProvider, authProvider, dataProvider, i18nProvider })
    }
    start()
  }, [setState])

  if (!state) {
    return null
  }

  if (routerRef.current?.state !== state) {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30 * 1000, // 30 seconds
          retry: 0,
        },
      },
    })
    const router = createBrowserRouter(
      [
        {
          path: '/*',
          element: (
            <Admin
              requireAuth={true}
              authProvider={state.authProvider}
              catchAll={NotFound}
              dataProvider={state.dataProvider}
              disableTelemetry
              queryClient={queryClient}
              theme={lightTheme}
              darkTheme={darkTheme}
              i18nProvider={state.i18nProvider}
              layout={Layout}
              loginPage={LoginPage}
              store={store}
            >
              {customRoutes.map((route, index) => (
                <CustomRoutes key={index} noLayout={Boolean(route.noLayout)}>
                  <Route path={route.element.path} element={<route.element />} />
                </CustomRoutes>
              ))}
              {(permissions) => resources.map((r) => getResourceWithPermissions(r, permissions))}
            </Admin>
          ),
        },
      ],
      { basename: state.basename },
    )
    routerRef.current = { state, router }
  }

  return (
    <GoogleMapsAPIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? env.GOOGLE_MAPS_API_KEY}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <ApiProvider value={state.apiProvider}>
          <AccountsProvider value={state.accountsProvider}>
            <RouterProvider router={routerRef.current.router} />
          </AccountsProvider>
        </ApiProvider>
      </LocalizationProvider>
    </GoogleMapsAPIProvider>
  )
}

export default App
